import { type CSSProperties } from 'react';
import {
  EdgeLabelRenderer,
  SmoothStepEdge,
  StraightEdge,
  getSmoothStepPath,
  getStraightPath,
  type EdgeProps,
  type SmoothStepPathOptions,
} from '@xyflow/react';

import { isManualLineEdge } from '../lineLinkValidation';
import { useMatchedLineEdgeActions } from './matchedLineEdgeActions';
import { UnlinkEdgeControl } from './UnlinkEdgeControl';

/**
 * #1344 — selectable green/amber line↔line match edge.
 *
 * The pre-#1344 matched-line edges were plain builtin ``smoothstep`` /
 * ``straight`` edges carrying an inline ``style.stroke``. React Flow
 * paints its selection feedback via the ``.react-flow__edge.selected``
 * CSS class, but an **inline** ``style.stroke`` wins the cascade over
 * that class — so a selected matched edge looked identical to an
 * unselected one. Operators clicked, saw no change, and never trusted
 * Backspace/Delete to unlink (the unlink plumbing itself already
 * existed: ``onEdgesDelete`` → ``handleEdgesDelete`` → ``onManualUnlink``).
 *
 * Fix: render these edges through this custom edge type so the stroke
 * colour / width react to the ``selected`` prop React Flow passes in,
 * and so we can widen the invisible ``interactionWidth`` hit target for
 * the thin 1.25px line. Geometry is untouched — we delegate to the SAME
 * builtin ``SmoothStepEdge`` (matched, cornered) / ``StraightEdge``
 * (near-match, diagonal) components React Flow would have rendered, with
 * the same ``pathOptions``, so the carefully-tuned #1159/#1174 routing
 * does not regress.
 *
 * HOF-216 — #1344's fix was to the SELECTION, not to the TRIGGER, and the
 * founder rediscovered the unlink as missing on 2026-08-31. A selected edge
 * now also paints a visible ``Verknüpfung lösen ✕`` control at its midpoint
 * (``EdgeLabelRenderer``), whose tooltip names the Backspace/Delete shortcut
 * so the shortcut becomes discoverable rather than replaced.
 */

/** Geometry + colour carried on the edge ``data`` so the component can
 * pick the right builtin renderer and base stroke. ``buildMatchedLineEdge``
 * is the sole producer. */
export interface MatchedLineEdgeData {
  /** ``true`` → green cornered smoothstep; ``false`` → amber straight diagonal. */
  matched: boolean;
  /** Base (unselected) stroke colour. */
  stroke: string;
  /** smoothstep bend options (only for the matched geometry). */
  pathOptions?: SmoothStepPathOptions;
  // Index signature — React Flow's ``Edge['data']`` is ``Record<string, unknown>``.
  [key: string]: unknown;
}

/** Base (unselected) stroke width for a matched-line edge. */
export const MATCHED_LINE_BASE_STROKE_WIDTH = 1.25;
/** Bolder stroke width when the edge is selected (armed for delete). */
export const MATCHED_LINE_SELECTED_STROKE_WIDTH = 3;
/**
 * Selected stroke colour — a brighter/darker-saturated variant of the
 * base so the operator sees a clear "armed" state. Green ``#10b981`` →
 * ``#047857`` (emerald-700), amber ``#f59e0b`` → ``#b45309`` (amber-700).
 */
const SELECTED_STROKE: Record<string, string> = {
  '#10b981': '#047857',
  '#f59e0b': '#b45309',
};

/**
 * Generous invisible hit-target width so the 1.25px line is comfortably
 * clickable. React Flow renders a transparent path of this width on top
 * of the visible stroke and routes pointer events to it.
 */
export const MATCHED_LINE_INTERACTION_WIDTH = 24;

/**
 * Pure selection-aware style. Exported so the select→highlight contract
 * is unit-testable without mounting React Flow.
 *
 * - Unselected → the base stroke at 1.25px (unchanged from pre-#1344).
 * - Selected → a brighter stroke at 3px so the operator can SEE which
 *   edge Backspace/Delete will unlink.
 */
export function computeMatchedLineEdgeStyle(params: {
  stroke: string;
  selected: boolean;
}): CSSProperties {
  if (!params.selected) {
    return { stroke: params.stroke, strokeWidth: MATCHED_LINE_BASE_STROKE_WIDTH };
  }
  return {
    stroke: SELECTED_STROKE[params.stroke] ?? params.stroke,
    strokeWidth: MATCHED_LINE_SELECTED_STROKE_WIDTH,
  };
}

/**
 * HOF-216 — does this edge paint the visible unlink control?
 *
 * Three conditions, and the third is the one that matters: ``isManualLineEdge``
 * stays the SINGLE predicate deciding which edges carry an unlink, for the
 * on-edge control, the context menu and the Backspace/Delete shortcut alike.
 * A document-level edge (the dotted grey GS→LS REF/POS edge, an Abrechnung
 * edge, a stub) has no unlink at any layer — that half is HOF-219 — and
 * ``hasActions`` false is a Storybook / standalone / read-only mount, which
 * shows no control at all (AC 7), matching the pre-existing
 * ``onManualUnlink ? … : undefined`` gate on ``deleteKeyCode``.
 *
 * Pure + exported so the matrix is unit-testable without mounting ReactFlow
 * (``EdgeLabelRenderer`` portals into a DOM node only a live ``<ReactFlow>``
 * provides, so a mounted assertion here would be a canvas test, not an
 * edge test).
 */
export function shouldRenderUnlinkControl(params: {
  selected: boolean;
  hasActions: boolean;
  sourceHandle: string | null | undefined;
  targetHandle: string | null | undefined;
}): boolean {
  if (!params.hasActions || !params.selected) return false;
  return isManualLineEdge({ sourceHandle: params.sourceHandle, targetHandle: params.targetHandle });
}

/**
 * Custom edge component registered under the ``matchedLine`` edge type.
 * Reads ``selected`` + ``data`` and delegates to the matching builtin
 * edge renderer, injecting the selection-aware ``style`` and the wide
 * ``interactionWidth``.
 */
export function MatchedLineEdge(props: EdgeProps) {
  const data = (props.data ?? {}) as Partial<MatchedLineEdgeData>;
  const stroke = data.stroke ?? '#10b981';
  const matched = data.matched ?? true;
  const style = computeMatchedLineEdgeStyle({ stroke, selected: !!props.selected });
  const actions = useMatchedLineEdgeActions();

  const showControl = shouldRenderUnlinkControl({
    selected: !!props.selected,
    hasActions: !!actions,
    sourceHandle: props.sourceHandleId,
    targetHandle: props.targetHandleId,
  });

  // Re-derive the label anchor from the SAME geometry helper the builtin
  // renderer below uses, rather than averaging the endpoints: on a cornered
  // smoothstep the midpoint of the straight line between the handles can sit
  // well off the drawn path, which would float the control over a card.
  const [, labelX, labelY] = matched
    ? getSmoothStepPath({
        sourceX: props.sourceX,
        sourceY: props.sourceY,
        sourcePosition: props.sourcePosition,
        targetX: props.targetX,
        targetY: props.targetY,
        targetPosition: props.targetPosition,
        borderRadius: data.pathOptions?.borderRadius,
        offset: data.pathOptions?.offset,
      })
    : getStraightPath({
        sourceX: props.sourceX,
        sourceY: props.sourceY,
        targetX: props.targetX,
        targetY: props.targetY,
      });

  const control = showControl ? (
    <EdgeLabelRenderer>
      <div
        className="nodrag nopan"
        // ``EdgeLabelRenderer`` renders into an overlay with pointer events
        // off, so the control has to opt itself back in or it is decorative.
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          pointerEvents: 'all',
          zIndex: 10,
        }}
        data-testid={`unlink-edge-control-${props.id}`}
      >
        <UnlinkEdgeControl
          onActivate={() =>
            actions?.requestUnlink(
              {
                id: props.id,
                sourceHandle: props.sourceHandleId,
                targetHandle: props.targetHandleId,
              },
              'edge_control',
            )
          }
        />
      </div>
    </EdgeLabelRenderer>
  ) : null;

  if (matched) {
    return (
      <>
        <SmoothStepEdge
          {...props}
          style={style}
          pathOptions={data.pathOptions}
          interactionWidth={MATCHED_LINE_INTERACTION_WIDTH}
        />
        {control}
      </>
    );
  }
  return (
    <>
      <StraightEdge
        {...props}
        style={style}
        interactionWidth={MATCHED_LINE_INTERACTION_WIDTH}
      />
      {control}
    </>
  );
}
